import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { useStudentMiniCard } from 'core/hooks';
import { formatAmountsDue } from 'core/utils/currencyTotals';
import { LmsBlockBadge } from 'molecules';

import { StudentHoverCard } from './StudentHoverCard';
import * as S from './StudentMiniCard.styled';

interface IProps {
  studentId: string;
}

const MONEY_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Always-visible one-line student summary for the thread header (spec 0009):
 * first active group (+N), a red debt chip, financing flags and the LMS lock.
 * Clicking it opens the full mini-card (data already in the Apollo cache —
 * this strip and the popover share one query). Renders nothing while loading
 * or on error so the header stays clean.
 */
export const StudentContextStrip = memo<IProps>(({ studentId }) => {
  const { t } = useTranslation();
  const { card } = useStudentMiniCard(studentId);

  if (!card) return null;

  const active = card.groups.filter((g) => g.status === 'active');
  const shown = active.length ? active : card.groups;
  const primary = shown[0];
  const { finance } = card;
  const hasFlags = shown.some(
    (g) =>
      g.freeOfCharge === true ||
      g.statePrefinanced === true ||
      g.paymentByCompany === true,
  );

  return (
    <StudentHoverCard
      studentId={studentId}
      trigger="click"
      placement="bottomLeft"
    >
      <S.Strip type="button">
        {primary ? (
          <S.StripGroup>
            {primary.groupName}
            {shown.length > 1 ? ` +${shown.length - 1}` : ''}
          </S.StripGroup>
        ) : (
          <S.StripGroup>
            {t('pages.communicator.miniCard.groupsEmpty')}
          </S.StripGroup>
        )}
        {finance?.isDebtor ? (
          <S.StripDebt>
            {t('pages.communicator.miniCard.debtor')} ·{' '}
            {/* Просрочка по валютам (`overdueAmounts`), не одно число. */}
            {formatAmountsDue(finance.overdueAmounts) ||
              MONEY_FMT.format(0)} ·{' '}
            {t('pages.communicator.miniCard.overdueDays', {
              n: finance.maxOverdueDays,
            })}
          </S.StripDebt>
        ) : null}
        {hasFlags && (
          <S.FlagTag>{t('pages.communicator.miniCard.flagsShort')}</S.FlagTag>
        )}
        {card.isLmsBlocked && <LmsBlockBadge />}
      </S.Strip>
    </StudentHoverCard>
  );
});

StudentContextStrip.displayName = 'StudentContextStrip';
